import { node, string, func, bool, oneOf } from 'prop-types';
import styled from 'styled-components';

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 18px;
  height: 18px;
  margin-right: 8px;

  svg {
    height: auto;
    fill: ${({theme}) => theme.colors.white};
  }
`;

const kindStyles = ({ theme, kind, isFlat }) =>
  ({
    primary: `
      color: ${isFlat ? theme.colors.information : theme.colors.white};
      background-color: ${theme.colors.brand_blue};

      &:hover:not(:disabled) {
        color: ${isFlat ? theme.colors.link_hover : theme.colors.white};
        background-color: ${isFlat ? 'transparent' : theme.colors.link};
      }

      &:active,
      &:hover:active,
      &:focus:active {
        background-color: #0d6ca7;
      }

      &:focus {
        box-shadow: 0 0 0 0.2em rgba(17, 139, 214, 0.25);
      }
    `,
    secondary: `
      color: #696969;
      background-color: transparent;
      border: 1px solid #696969;

      svg {
        fill: #696969;
      }

      &:hover:not(:disabled) {
        color: ${isFlat ? '#9599a3' : theme.colors.white};
        background-color: ${isFlat ? 'transparent' : '#9599a3'};

        svg {
          fill: ${theme.colors.white};
        }
      }

      &:active,
      &:hover:active,
      &:focus:active {
        background-color: #515151;
        color: ${isFlat ? '#515151' : theme.colors.white};
        border: 1px solid transparent;

        svg {
          fill: ${theme.colors.white};
        }
      }

      &:focus {
        box-shadow: 0 0 0 0.2em rgba(32, 32, 32, 0.25);
      }
    `,
    attention: `
      color: ${isFlat ? theme.colors.attention : theme.colors.heading_dark};
      background-color: ${theme.colors.brand_yellow};

      svg {
        fill: ${theme.colors.heading_dark};
      }

      &:hover:not(:disabled) {
        color: ${isFlat ? '#fed648' : theme.colors.heading_dark};
        background-color: ${isFlat ? 'transparent' : '#fed648'};
      }

      &:active,
      &:hover:active,
      &:focus:active {
        background-color: #d2a502;
      }

      &:focus {
        box-shadow: 0 0 0 0.2em rgba(253, 200, 10, 0.25);
      }
    `,
    positive: `
      color: ${isFlat ? theme.colors.positive : theme.colors.white};
      background-color: ${theme.colors.positive};

      &:hover:not(:disabled) {
        color: ${isFlat ? '#8dc753' : theme.colors.white};
        background-color: ${isFlat ? 'transparent' : '#8dc753'};
      }

      &:active,
      &:hover:active,
      &:focus:active {
        background-color: #4d8713;
      }

      &:focus {
        box-shadow: 0 0 0 0.2em rgba(103, 180, 25, 0.25);
      }
    `,
    negative: `
      color: ${isFlat ? theme.colors.negative : theme.colors.white};
      background-color: ${theme.colors.negative};

      &:hover:not(:disabled) {
        color: ${isFlat ? '#e34b4b' : theme.colors.white};
        background-color: ${isFlat ? 'transparent' : '#e34b4b'};
      }

      &:active,
      &:hover:active,
      &:focus:active {
        background-color: #a01021;
      }

      &:focus {
        box-shadow: 0 0 0 0.2em rgba(207, 20, 43, 0.25);
      }
    `,
  }[kind]);

const buttonSizes = ({ size }) =>
  ({
    sm: `
      font-size: 0.8rem;
    `,
    md: `
      ${IconWrapper} {
        width: 20px;
        height: 20px;
        margin-right: 12px;
      }
    `,
    lg: `
      font-size: 1.25rem;

      ${IconWrapper} {
        width: 22px;
        height: 22px;
        margin-right: 12px;
      }
    `,
}[size]);


const StyledButton = styled.button`
  display: inline-flex;
  font-weight: 700;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  cursor: pointer;
  padding: 0 1em;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, padding 0.15s ease-in-out, box-shadow 0.15s ease-in-out, transform 0.15s ease-in-out, -webkit-transform 0.15s ease-in-out;

  span {
    padding: 0.75em 0;
  }

  &:disabled {
    opacity: 0.25;
  }

  ${(props) => kindStyles(props)};
  ${(props) => buttonSizes(props)};

  ${({ isFlat }) =>
    isFlat &&
    `
      background: none;
      border: 1px solid transparent;

      &:hover {
        background-color: transparent;
      }
    `};
`;

function Button({className, isFlat, children, icon, onClick, ...rest}) {

  return (
    <StyledButton className={className} isFlat={isFlat} onClick={onClick} {...rest}>
      {
        icon && !isFlat && (
          <IconWrapper>
            {icon}
          </IconWrapper>
        )
      }
      <span>{children}</span>
    </StyledButton>
  );
}

Button.propTypes = {
  children: node.isRequired,
  kind: oneOf(['primary', 'secondary', 'attention', 'positive', 'negative']),
  size: oneOf(['sm', 'md', 'lg']),
  isFlat: bool,
  onClick: func.isRequired,
  icon: node,
  className: string,
}

Button.defaultProps = {
  kind: 'primary',
  size: 'md',
  isFlat: false,
  icon: null,
  className: '',
}

export default styled(Button)``;